"use client";

import { useEffect, useRef, useState } from "react";
import { completeAuthHashSessionAction } from "@/lib/actions/auth-session";
import { parseAuthHashError } from "@/lib/auth/parse-auth-hash-error";
import { parseAuthHashSession } from "@/lib/auth/parse-auth-hash-session";
import { createClient } from "@/lib/supabase/client";

type HandlerStatus = "idle" | "loading" | "error";

/**
 * Consumes Supabase implicit-flow tokens in the URL hash (invite links that
 * land on /login or / instead of /auth/callback?code=).
 */
export function AuthHashSessionHandler() {
	const [status, setStatus] = useState<HandlerStatus>("idle");
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const startedRef = useRef(false);

	useEffect(() => {
		if (startedRef.current) return;

		const hashError = parseAuthHashError(window.location.hash);
		if (hashError && window.location.pathname !== "/login") {
			window.location.replace(`/login${window.location.hash}`);
			return;
		}

		const tokens = parseAuthHashSession(window.location.hash);
		if (!tokens) return;

		startedRef.current = true;
		setStatus("loading");

		async function establishSession() {
			const sessionTokens = tokens;
			if (!sessionTokens) return;

			const supabase = createClient();
			const { error: sessionError } = await supabase.auth.setSession({
				access_token: sessionTokens.access_token,
				refresh_token: sessionTokens.refresh_token ?? "",
			});

			if (sessionError) {
				setStatus("error");
				setErrorMessage(sessionError.message);
				startedRef.current = false;
				return;
			}

			const { pathname, search } = window.location;
			window.history.replaceState(null, "", `${pathname}${search}`);

			const result = await completeAuthHashSessionAction(sessionTokens.type);

			if (!result.success) {
				setStatus("error");
				setErrorMessage(result.error);
				startedRef.current = false;
				return;
			}

			window.location.assign(result.redirectTo);
		}

		void establishSession();
	}, []);

	if (status === "idle") return null;

	return (
		<output
			aria-live="polite"
			className="fixed inset-x-0 top-0 z-100 flex justify-center px-4 pt-4"
		>
			<div className="max-w-md rounded-lg border border-white/10 bg-zinc-950/95 px-4 py-3 text-center text-sm text-white shadow-lg backdrop-blur-sm">
				{status === "loading" ? (
					<p>Signing you in from your invite link…</p>
				) : (
					<p className="text-red-300">
						{errorMessage ??
							"Could not complete sign-in. Ask for a new invite or try again."}
					</p>
				)}
			</div>
		</output>
	);
}
